import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Terms.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Términos y Condiciones | Graphyra</title>
        <meta name="description" content="Términos y Condiciones de uso de la plataforma Graphyra" />
      </Helmet>

      <div className="legal-container">
        <div className="legal-header">
          <h1 className="legal-title">Términos y Condiciones</h1>
          <p className="legal-update">Última actualización: 3 de enero de 2026</p>
          <p className="legal-intro">
            Estos Términos y Condiciones (los "Términos") regulan el acceso y uso de Graphyra (la "Plataforma").
            Al crear una cuenta, acceder o usar la Plataforma, aceptas estos Términos en su totalidad.
            Si no estás de acuerdo con alguna parte de estos términos, no uses la Plataforma.
          </p>
          <div className="legal-notice">
            <strong>Aviso importante:</strong> Este documento es una plantilla informativa y debe ser revisado y 
            adaptado por asesoría legal conforme a tu jurisdicción, modelo de negocio y tratamiento real de datos.
          </div>
        </div>

        <nav className="legal-nav">
          <a href="#identificacion">1. Identificación</a>
          <a href="#definiciones">2. Definiciones</a>
          <a href="#registro">3. Registro</a>
          <a href="#uso">4. Uso Permitido</a>
          <a href="#contenido">5. Contenido</a>
          <a href="#moderacion">6. Moderación</a>
          <a href="#propiedad">7. Propiedad Intelectual</a>
          <a href="#seguridad">8. Seguridad</a>
          <a href="#terceros">9. Servicios de Terceros</a>
          <a href="#disponibilidad">10. Disponibilidad</a>
          <a href="#garantias">11. Garantías</a>
          <a href="#responsabilidad">12. Responsabilidad</a>
          <a href="#indemnidad">13. Indemnidad</a>
          <a href="#terminacion">14. Terminación</a>
          <a href="#ley">15. Ley Aplicable</a>
          <a href="#cambios">16. Cambios</a>
          <a href="#contacto">17. Contacto</a>
        </nav>

        <div className="legal-content">
          <section id="identificacion" className="legal-section">
            <h2>1. Identificación del Responsable</h2>
            <div className="info-box">
              <p><strong>Titular/Responsable:</strong> Luis Cepeda ("Graphyra", "nosotros")</p>
              <p><strong>Teléfono:</strong> +57 3115491733</p>
              <p><strong>Correo de contacto:</strong> contact.graphyra@gmail.com</p>
              <p><strong>Correo de soporte:</strong> support.graphyra@gmail.com</p>
              <p><strong>Sitio web:</strong> www.graphyra.com</p>
            </div>
          </section>

          <section id="definiciones" className="legal-section">
            <h2>2. Definiciones</h2>
            <p>Para efectos de estos Términos, se entenderá por:</p>
            <div className="definition-list">
              <div className="definition-item">
                <strong>Usuario:</strong>
                <p>Toda persona física que accede, se registra o utiliza la Plataforma de cualquier forma.</p>
              </div>
              <div className="definition-item">
                <strong>Contenido:</strong>
                <p>Toda información, texto, imagen, video, audio, publicación, comentario, reacción u otro material 
                   generado, publicado o compartido en la Plataforma.</p>
              </div>
              <div className="definition-item">
                <strong>Cuenta:</strong>
                <p>Conjunto de credenciales (usuario, email, contraseña) y perfil personal del Usuario para acceder 
                   a funcionalidades autenticadas de la Plataforma.</p>
              </div>
              <div className="definition-item">
                <strong>Plataforma:</strong>
                <p>El sitio web Graphyra y todos sus servicios, funcionalidades, aplicaciones y contenidos asociados.</p>
              </div>
            </div>
          </section>

          <section id="registro" className="legal-section">
            <h2>3. Requisitos y Registro de Cuenta</h2>
            <h3>3.1. Requisitos de Elegibilidad</h3>
            <ul className="legal-list">
              <li>Debes ser mayor de edad según las leyes de tu jurisdicción (generalmente 18 años).</li>
              <li>Si eres menor de edad, debes contar con autorización expresa de tu representante legal.</li>
              <li>Debes tener capacidad legal para celebrar contratos vinculantes.</li>
            </ul>

            <h3>3.2. Registro y Veracidad de la Información</h3>
            <ul className="legal-list">
              <li>Al crear una Cuenta, debes proporcionar información veraz, precisa, actual y completa.</li>
              <li>Debes mantener actualizada la información de tu perfil.</li>
              <li>No puedes hacerte pasar por otra persona o entidad, ni suplantar identidades.</li>
              <li>No puedes crear múltiples cuentas con fines abusivos o fraudulentos.</li>
            </ul>

            <h3>3.3. Seguridad de la Cuenta</h3>
            <ul className="legal-list">
              <li>Eres el único responsable de mantener la confidencialidad de tu contraseña.</li>
              <li>Eres responsable de todas las actividades que ocurran bajo tu Cuenta.</li>
              <li>Debes notificarnos inmediatamente si detectas uso no autorizado de tu Cuenta.</li>
              <li>No debes compartir tus credenciales con terceros ni permitir que otros accedan a tu Cuenta.</li>
            </ul>
          </section>

          <section id="uso" className="legal-section">
            <h2>4. Uso Permitido y Conductas Prohibidas</h2>
            <h3>4.1. Uso Permitido</h3>
            <p>La Plataforma está diseñada para:</p>
            <ul className="legal-list">
              <li>Compartir contenido científico, educativo, cultural y de entretenimiento.</li>
              <li>Participar en comunidades de aprendizaje y discusión.</li>
              <li>Acceder a recursos académicos, artículos y material audiovisual.</li>
              <li>Interactuar de manera respetuosa y constructiva con otros usuarios.</li>
            </ul>

            <h3>4.2. Conductas Estrictamente Prohibidas</h3>
            <div className="warning-box">
              <h4>🚫 Actividades Ilegales y Fraude</h4>
              <ul className="legal-list">
                <li>Usar la Plataforma para actividades ilegales, fraudulentas o criminales.</li>
                <li>Violar derechos de terceros (propiedad intelectual, privacidad, honor).</li>
                <li>Distribuir contenido que infrinja leyes locales, nacionales o internacionales.</li>
              </ul>
            </div>

            <div className="warning-box">
              <h4>🚫 Contenido Prohibido</h4>
              <ul className="legal-list">
                <li>Contenido difamatorio, calumnioso, acosador o intimidante.</li>
                <li>Contenido discriminatorio por raza, etnia, religión, género, orientación sexual o discapacidad.</li>
                <li>Contenido sexualmente explícito, pornográfico o de explotación sexual.</li>
                <li>Contenido que incite al odio, violencia, autolesión o actividades peligrosas.</li>
                <li>Información falsa, engañosa o que constituya desinformación deliberada.</li>
              </ul>
            </div>

            <div className="warning-box">
              <h4>🚫 Actividades Técnicas Prohibidas</h4>
              <ul className="legal-list">
                <li>Intentar acceder sin autorización a sistemas, cuentas, datos o redes.</li>
                <li>Realizar scraping, crawling o extracción masiva de datos sin permiso.</li>
                <li>Usar bots, scripts o automatizaciones abusivas para manipular la Plataforma.</li>
                <li>Distribuir malware, virus, ransomware, phishing o cualquier código malicioso.</li>
                <li>Realizar ataques de denegación de servicio (DDoS) o similares.</li>
                <li>Manipular métricas (likes, comentarios, visualizaciones) mediante medios artificiales.</li>
              </ul>
            </div>

            <div className="warning-box">
              <h4>🚫 Spam y Publicidad No Autorizada</h4>
              <ul className="legal-list">
                <li>Enviar spam, correos masivos no solicitados o publicidad intrusiva.</li>
                <li>Publicar contenido comercial o promocional sin autorización expresa.</li>
                <li>Usar la Plataforma para esquemas piramidales, estafas o fraudes.</li>
              </ul>
            </div>
          </section>

          <section id="contenido" className="legal-section">
            <h2>5. Contenido del Usuario (UGC)</h2>
            <h3>5.1. Responsabilidad por el Contenido</h3>
            <p>Como Usuario, eres el único y exclusivo responsable de:</p>
            <ul className="legal-list">
              <li>Todo el Contenido que publiques, compartas o distribuyas en la Plataforma.</li>
              <li>Garantizar que posees todos los derechos necesarios sobre el Contenido.</li>
              <li>Asegurarte de que tu Contenido no infringe derechos de terceros.</li>
              <li>El cumplimiento de leyes aplicables respecto a tu Contenido.</li>
            </ul>

            <h3>5.2. Licencia de Contenido</h3>
            <p>
              Al publicar Contenido en la Plataforma, nos otorgas una licencia <strong>no exclusiva, mundial, libre de regalías, 
              sublicenciable y transferible</strong> para:
            </p>
            <ul className="legal-list">
              <li>Alojar, almacenar, reproducir y mostrar tu Contenido.</li>
              <li>Distribuir y comunicar públicamente tu Contenido dentro de la Plataforma.</li>
              <li>Adaptar y modificar técnicamente el Contenido para su correcta visualización (formatos, resoluciones).</li>
              <li>Crear copias de respaldo y cache para asegurar la operación del servicio.</li>
            </ul>
            <p className="legal-note">
              Esta licencia es necesaria exclusivamente para operar el servicio y finaliza cuando elimines tu Contenido, 
              salvo por copias de respaldo técnicas necesarias durante períodos razonables.
            </p>

            <h3>5.3. Derechos que Conservas</h3>
            <p>Conservas todos los derechos de propiedad intelectual sobre tu Contenido original.</p>
          </section>

          <section id="moderacion" className="legal-section">
            <h2>6. Moderación, Reportes y Medidas Disciplinarias</h2>
            <h3>6.1. Moderación de Contenido</h3>
            <p>Nos reservamos el derecho, pero no la obligación, de:</p>
            <ul className="legal-list">
              <li>Revisar, monitorear y moderar Contenido publicado en la Plataforma.</li>
              <li>Limitar la visibilidad, marcar como sensible o retirar Contenido que infrinja estos Términos.</li>
              <li>Utilizar herramientas automatizadas y revisión manual para detectar contenido prohibido.</li>
              <li>Tomar decisiones de moderación según nuestro criterio razonable.</li>
            </ul>

            <h3>6.2. Sistema de Reportes</h3>
            <p>Los usuarios pueden reportar Contenido o conductas que consideren inapropiadas. Evaluaremos cada reporte 
               y tomaremos las acciones que consideremos necesarias.</p>

            <h3>6.3. Medidas Disciplinarias</h3>
            <p>En caso de infracciones, podemos aplicar medidas que incluyen, sin limitarse a:</p>
            <div className="warning-list">
              <div className="warning-item">
                <strong>⚠️ Advertencia:</strong> Notificación sobre conducta inapropiada.
              </div>
              <div className="warning-item">
                <strong>🗑️ Eliminación de Contenido:</strong> Remoción de publicaciones que violen los Términos.
              </div>
              <div className="warning-item">
                <strong>⏸️ Restricción Temporal:</strong> Limitación de funcionalidades por período determinado.
              </div>
              <div className="warning-item">
                <strong>🚫 Suspensión de Cuenta:</strong> Desactivación temporal de la cuenta.
              </div>
              <div className="warning-item">
                <strong>❌ Cancelación Definitiva:</strong> Eliminación permanente de la cuenta en casos graves o reincidencia.
              </div>
            </div>
            <p className="legal-note">
              La moderación puede no ser inmediata y no garantizamos la detección total de contenido inapropiado. 
              La aplicación de medidas es a nuestra discreción razonable.
            </p>
          </section>

          <section id="propiedad" className="legal-section">
            <h2>7. Propiedad Intelectual</h2>
            <h3>7.1. Propiedad de la Plataforma</h3>
            <p>Todos los elementos de la Plataforma son propiedad de Graphyra o sus licenciantes:</p>
            <ul className="legal-list">
              <li>Marca, logotipos, nombres comerciales y elementos distintivos.</li>
              <li>Diseño, interfaz gráfica y experiencia de usuario.</li>
              <li>Código fuente, software, algoritmos y arquitectura técnica.</li>
              <li>Base de datos, estructura y organización de información.</li>
              <li>Documentación, tutoriales y materiales propios.</li>
            </ul>

            <h3>7.2. Uso Limitado</h3>
            <p>
              Te otorgamos una licencia limitada, revocable y no exclusiva para usar la Plataforma según estos Términos.
              No se concede ningún derecho sobre marcas, diseños o elementos de propiedad intelectual más allá del uso personal.
            </p>

            <h3>7.3. Protección Legal</h3>
            <p>
              Todos los elementos de propiedad intelectual están protegidos por leyes de derechos de autor, marcas, 
              patentes y otras normativas aplicables a nivel nacional e internacional.
            </p>
          </section>

          <section id="seguridad" className="legal-section">
            <h2>8. Seguridad, Verificación y Prevención de Abuso</h2>
            <h3>8.1. Medidas de Seguridad</h3>
            <p>Implementamos medidas técnicas y organizativas para proteger la Plataforma:</p>
            <ul className="legal-list">
              <li>Sistemas de verificación como reCAPTCHA para prevenir bots y automatizaciones abusivas.</li>
              <li>Monitoreo de actividad sospechosa y patrones de abuso.</li>
              <li>Encriptación de contraseñas y datos sensibles.</li>
              <li>Sistemas de detección de intrusiones y amenazas.</li>
            </ul>

            <h3>8.2. Bloqueos y Restricciones</h3>
            <p>
              Podemos bloquear temporalmente o permanentemente solicitudes, direcciones IP o cuentas si detectamos:
            </p>
            <ul className="legal-list">
              <li>Actividad automatizada excesiva o no autorizada.</li>
              <li>Intentos de acceso no autorizado o ataques.</li>
              <li>Patrones de uso que amenacen la integridad o disponibilidad del servicio.</li>
              <li>Comportamiento fraudulento o malicioso.</li>
            </ul>
          </section>

          <section id="terceros" className="legal-section">
            <h2>9. Servicios y Enlaces de Terceros</h2>
            <p>
              La Plataforma puede integrar servicios de terceros (como Google reCAPTCHA) o contener enlaces a sitios externos.
            </p>
            <div className="info-box">
              <h4>ℹ️ Importante sobre Terceros:</h4>
              <ul className="legal-list">
                <li>No controlamos ni somos responsables de productos, servicios o políticas de terceros.</li>
                <li>El uso de servicios de terceros se rige por sus propios términos y políticas.</li>
                <li>No garantizamos la disponibilidad, seguridad o contenido de sitios de terceros.</li>
                <li>Te recomendamos revisar las políticas de privacidad de servicios de terceros que utilices.</li>
              </ul>
            </div>
          </section>

          <section id="disponibilidad" className="legal-section">
            <h2>10. Disponibilidad, Cambios y Mantenimiento</h2>
            <h3>10.1. Disponibilidad del Servicio</h3>
            <p>
              Aunque nos esforzamos por mantener la Plataforma disponible 24/7, no garantizamos disponibilidad 
              ininterrumpida ni libre de errores.
            </p>

            <h3>10.2. Mantenimiento y Actualizaciones</h3>
            <p>Podemos:</p>
            <ul className="legal-list">
              <li>Realizar mantenimiento programado o de emergencia con o sin aviso previo.</li>
              <li>Actualizar, modificar o mejorar funcionalidades de la Plataforma.</li>
              <li>Agregar, modificar o eliminar características según nuestras necesidades operativas.</li>
              <li>Suspender temporalmente el servicio por razones técnicas, de seguridad o legales.</li>
            </ul>

            <h3>10.3. Cambios en el Servicio</h3>
            <p>
              Nos reservamos el derecho de modificar, suspender o descontinuar cualquier aspecto de la Plataforma, 
              total o parcialmente, en cualquier momento y sin responsabilidad hacia los usuarios.
            </p>
          </section>

          <section id="garantias" className="legal-section">
            <h2>11. Exclusión de Garantías</h2>
            <div className="warning-box">
              <p>
                <strong>LA PLATAFORMA SE PROPORCIONA "TAL CUAL" Y "SEGÚN DISPONIBILIDAD".</strong> En la medida máxima 
                permitida por la ley aplicable, Graphyra <strong>NO OTORGA GARANTÍAS</strong> de ningún tipo, expresas 
                o implícitas, incluyendo pero no limitándose a:
              </p>
              <ul className="legal-list">
                <li>Garantías de comerciabilidad o idoneidad para un propósito particular.</li>
                <li>Garantías de no infracción de derechos de terceros.</li>
                <li>Garantías de disponibilidad continua, ininterrumpida o libre de errores.</li>
                <li>Garantías de exactitud, completitud o actualidad del contenido.</li>
                <li>Garantías de seguridad absoluta o ausencia de vulnerabilidades.</li>
              </ul>
            </div>
            <p className="legal-note">
              Usas la Plataforma bajo tu propio riesgo. No garantizamos resultados específicos ni que la Plataforma 
              satisfaga tus necesidades particulares.
            </p>
          </section>

          <section id="responsabilidad" className="legal-section">
            <h2>12. Limitación de Responsabilidad</h2>
            <div className="warning-box">
              <p>
                <strong>EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY</strong>, Graphyra y sus directivos, empleados, 
                socios, licenciantes y proveedores de servicios <strong>NO SERÁN RESPONSABLES</strong> por:
              </p>
              <ul className="legal-list">
                <li><strong>Daños indirectos, incidentales, especiales, consecuentes o punitivos</strong>, incluyendo 
                    pérdida de beneficios, datos, uso, fondo de comercio u otras pérdidas intangibles.</li>
                <li><strong>Daños derivados de:</strong> tu acceso, uso o incapacidad de usar la Plataforma; conducta 
                    o contenido de terceros; acceso no autorizado a tus transmisiones o datos; errores o interrupciones 
                    del servicio.</li>
                <li><strong>Cualquier reclamo de terceros</strong> relacionado con tu uso de la Plataforma o tu Contenido.</li>
              </ul>
            </div>
            <p className="legal-note">
              Algunas jurisdicciones no permiten la exclusión o limitación de ciertos daños, por lo que estas 
              limitaciones pueden no aplicar en tu caso. En tales jurisdicciones, nuestra responsabilidad se limitará 
              al máximo permitido por ley.
            </p>
          </section>

          <section id="indemnidad" className="legal-section">
            <h2>13. Indemnidad</h2>
            <p>
              Aceptas <strong>defender, indemnizar y mantener indemne</strong> a Graphyra, sus afiliados, directivos, 
              empleados, agentes, licenciantes y proveedores de servicios frente a cualquier reclamo, demanda, pérdida, 
              responsabilidad y gasto (incluyendo honorarios razonables de abogados) que surjan de o se relacionen con:
            </p>
            <ul className="legal-list">
              <li>Tu uso o mal uso de la Plataforma.</li>
              <li>Tu violación de estos Términos.</li>
              <li>Tu Contenido y cualquier infracción de derechos de terceros.</li>
              <li>Tu violación de leyes o regulaciones aplicables.</li>
              <li>Reclamaciones de que tu Contenido causó daños a terceros.</li>
            </ul>
            <p className="legal-note">
              Esta obligación de indemnidad sobrevive a la terminación de estos Términos y de tu uso de la Plataforma.
            </p>
          </section>

          <section id="terminacion" className="legal-section">
            <h2>14. Terminación</h2>
            <h3>14.1. Terminación por el Usuario</h3>
            <p>
              Puedes dejar de usar la Plataforma y cerrar tu Cuenta en cualquier momento contactándonos o usando 
              las funcionalidades disponibles en tu perfil.
            </p>

            <h3>14.2. Terminación por Graphyra</h3>
            <p>
              Podemos suspender o terminar tu Cuenta o acceso a la Plataforma, en cualquier momento y sin previo 
              aviso, si:
            </p>
            <ul className="legal-list">
              <li>Incumples estos Términos o nuestras políticas.</li>
              <li>Detectamos actividad fraudulenta, abusiva o maliciosa.</li>
              <li>Consideramos necesario por razones legales, de seguridad o técnicas.</li>
              <li>Lo exige una autoridad competente o proceso legal.</li>
            </ul>

            <h3>14.3. Efecto de la Terminación</h3>
            <p>Al terminar tu Cuenta:</p>
            <ul className="legal-list">
              <li>Perderás acceso a todas las funcionalidades de usuario autenticado.</li>
              <li>Tu Contenido puede ser eliminado según nuestras políticas de retención.</li>
              <li>Ciertas cláusulas sobrevivirán: propiedad intelectual, limitaciones de responsabilidad, 
                  indemnidad, ley aplicable y resolución de disputas.</li>
            </ul>
          </section>

          <section id="ley" className="legal-section">
            <h2>15. Ley Aplicable y Jurisdicción</h2>
            <div className="info-box">
              <p>
                <strong>Ley Aplicable:</strong> Estos Términos se rigen e interpretan de acuerdo con las leyes de 
                <strong> [PAÍS/ESTADO]</strong>, sin dar efecto a sus disposiciones sobre conflictos de leyes.
              </p>
              <p>
                <strong>Jurisdicción:</strong> Cualquier disputa, controversia o reclamo que surja de o se relacione 
                con estos Términos o su incumplimiento se someterá a la jurisdicción exclusiva de los tribunales de 
                <strong> [CIUDAD/JURISDICCIÓN]</strong>, salvo que la ley aplicable establezca disposiciones 
                imperativas diferentes.
              </p>
            </div>

            <h3>15.1. Resolución de Disputas</h3>
            <p>
              Antes de iniciar cualquier procedimiento legal, las partes acuerdan intentar resolver la disputa 
              mediante negociación directa de buena fe.
            </p>

            <h3>15.2. Renuncia a Acciones Colectivas</h3>
            <p className="legal-note">
              En la medida permitida por la ley, aceptas que cualquier disputa se resolverá de manera individual y 
              renuncias a participar en demandas colectivas o arbitrajes colectivos.
            </p>
          </section>

          <section id="cambios" className="legal-section">
            <h2>16. Cambios a los Términos</h2>
            <h3>16.1. Derecho a Modificar</h3>
            <p>
              Nos reservamos el derecho de modificar, actualizar o reemplazar estos Términos en cualquier momento. 
              Los cambios serán efectivos inmediatamente después de su publicación en esta página.
            </p>

            <h3>16.2. Notificación de Cambios</h3>
            <p>Para cambios importantes, haremos esfuerzos razonables para notificarte mediante:</p>
            <ul className="legal-list">
              <li>Aviso destacado en la Plataforma.</li>
              <li>Correo electrónico a la dirección registrada en tu Cuenta.</li>
              <li>Notificación dentro de la aplicación.</li>
            </ul>

            <h3>16.3. Aceptación de Cambios</h3>
            <p>
              El uso continuado de la Plataforma después de la publicación de cambios constituye tu aceptación de 
              los Términos modificados. Si no aceptas los cambios, debes dejar de usar la Plataforma.
            </p>

            <p className="legal-note">
              <strong>Fecha de vigencia:</strong> La versión actual de estos Términos está vigente desde la fecha 
              indicada al inicio de este documento.
            </p>
          </section>

          <section id="contacto" className="legal-section">
            <h2>17. Contacto</h2>
            <p>
              Para cualquier pregunta, comentario o solicitud relacionada con estos Términos y Condiciones, puedes 
              contactarnos a través de:
            </p>
            <div className="contact-box">
              <p><strong>📧 Correo de contacto:</strong> contact.graphyra@gmail.com</p>
              <p><strong>📧 Correo de soporte:</strong> support.graphyra@gmail.com</p>
              <p><strong>📧 Correo administrativo:</strong> admin.graphyra@gmail.com</p>
              <p><strong>📞 Teléfono:</strong> +57 3115491733</p>
              <p><strong>🌐 Formulario de contacto:</strong> Disponible en nuestra <Link to="/contact">página de contacto</Link></p>
            </div>
            <p className="legal-note">
              Responderemos a tu solicitud en un plazo razonable. Para consultas sobre privacidad y protección de datos, 
              consulta nuestra <Link to="/privacy">Política de Privacidad</Link>.
            </p>
          </section>

          <div className="legal-footer">
            <p>
              Este documento constituye el acuerdo completo entre tú y Graphyra respecto al uso de la Plataforma y 
              reemplaza cualquier acuerdo anterior.
            </p>
            <div className="legal-links">
              <Link to="/privacy">Política de Privacidad</Link>
              <span className="separator">•</span>
              <Link to="/cookies">Política de Cookies</Link>
              <span className="separator">•</span>
              <Link to="/">Volver al inicio</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
